import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import Layout from "./theme/Layout";
import "./App.css";
import ProtectedRoute from "./routes/ProtectedRoute";
import { useTranslation } from "react-i18next";
import LoaderSpinner from "./shared/sharedComponents/loaderSpinner/LoaderSpinner";
import { useSelector } from "react-redux";
import {
  getLocalStorageData,
  setLocalStorageData,
} from "./shared/commonFunction";
import GoogleAnalytics from "./google-analytics/GoogleAnalytics";
import GlobalChatWrapper from "./shared/sharedComponents/GlobalChatDrawer/GlobalChatWrapper";

const Login = lazy(() => import("./pages/Login/Login"));
const VerifyOtp = lazy(() => import("./pages/Verify-otp/VerifyOtp"));
const TermsAndConditions = lazy(() =>
  import("./pages/Terms-and-conditions/TermsAndConditions")
);
const AboutUsDetails = lazy(() =>
  import("./components/About-us-details/AboutUsDetails")
);
const DeleteAccount = lazy(() =>
  import("./pages/deleteAccountPage/DeleteAccount")
);
const CartPage = lazy(() =>
  import("./components/market-place/Cart-page/CartPage")
);
const OrdersList = lazy(() =>
  import("./components/market-place/Order-page/Orders-List/OrdersList")
);
const AllProductsPage = lazy(() =>
  import("./components/market-place/All-Products/AllProductPage")
);
const Rewards = lazy(() => import("./components/Rewards/Rewards"));

const OrderDetails = lazy(() =>
  import("./components/market-place/Order-page/Order-Details-Page/OrderDetails")
);

const Events = lazy(() => import("./components/Events/Events"));

const OneToOneGameRules = lazy(() =>
  import(
    "./components/One-to-one-games/One-to-one-game-rules/OneToOneGameRules"
  )
);
const OneToOneGames = lazy(() =>
  import("./components/One-to-one-games/OneToOneGames")
);
const ScoreSubmissionOneToOne = lazy(() =>
  import(
    "./components/One-to-one-games/Score-submission-one-to-one/ScoreSubmissionOneToOne"
  )
);

const OneToOneGameRoom = lazy(() =>
  import("./components/One-to-one-games/One-to-one-game-room/OneToOneGameRoom")
);
const OneToOneRoomDetails = lazy(() =>
  import("./components/One-to-one-games/Room-details/OneToOneRoomDetails")
);

const Wallet = lazy(() => import("./components/user-profile/wallet/Wallet"));

const Result = lazy(() => import("./components/Result/Result"));
const Tournaments = lazy(() => import("./components/Tournament/Tournaments"));
const EditUserProfile = lazy(() =>
  import("./components/user-profile/edit-user-profile/EditUserProfile")
);
const JoinTournament = lazy(() =>
  import("./components/Tournament/join-tournament/JoinTournament")
);
const Message = lazy(() => import("./components/Chat/Messages/Message"));
const HomePage = lazy(() => import("./components/HomePage"));
const FeatureTournament = lazy(() =>
  import("./components/Feature-tournament/FeatureTournament")
);
const LeaderShipBoard = lazy(() =>
  import("./components/Leader-ship-board/LeaderShipBoard")
);
const Academy = lazy(() => import("./components/Academy/Academy"));
const Consumerprofile = lazy(() =>
  import("./components/Consumer-profile/Consumer-Profile")
);
const FeaturedRegiter = lazy(() =>
  import("./components/Register-featured/RegisterFeatured")
);
const RegisterEvent = lazy(() =>
  import("./components/Events/Register-event/RegisterEvent")
);

const ContactUs = lazy(() => import("./components/Contact-us/ContactUs"));
// const AboutUs = lazy(() => import("./components/About-us/AboutUs"));
const JoinOurCommunty = lazy(() =>
  import("./components/Join-our-community/JoinOurCommunity")
);

const SignUp = lazy(() => import("./pages/Sign-up/SignUp"));
const ForgotPassword = lazy(() =>
  import("./pages/Forgot-password/ForgotPassword")
);

const Store = lazy(() =>
  import("./components/market-place/Store-Page/StorePage")
);

const ProductDetailsPage = lazy(() =>
  import("./components/market-place/Product-detail-page/ProductDetailsPage")
);

const AllAchievements = lazy(() =>
  import("./components/Achievements/AllAchievements")
);
const SendGift = lazy(() => import("./components/Gift/SendGift"));
const Friends = lazy(() => import("./components/Friends/Friends"));
const AllTeams = lazy(() => import("./components/Teams/AllTeams"));
const AdminChatReactions = lazy(() =>
  import("./components/Admin/AdminChatReactions")
);
const AllGameTypes = lazy(() => import("./components/GameTypes/AllGameTypes"));
const OneToOneLobby = lazy(() =>
  import("./components/One-to-one-games/One-to-one-lobby/OneToOneLobby")
);

const CreateDuelRoom = lazy(() =>
  import("./components/One-to-one-games/Create-duels-room/CreateDuelRoom")
);

const RandomPlay = lazy(() =>
  import("./components/One-to-one-games/Random-play/RandomPlay")
);

const AllRooms = lazy(() =>
  import("./components/One-to-one-games/Duels-room/DuelsRooms")
);

const LoadingFallback = () => {
  const { t } = useTranslation("common");
  return <div className="lazy-loading">{t("menu.loading")}</div>;
};

const NotFound = () => {
  const { t } = useTranslation("common");
  return (
    <h1 style={{ color: "#fff", textAlign: "center" }}>
      {t("website_heading.page_not_found")}
    </h1>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <><Layout /><GlobalChatWrapper /></>,
    children: [
      {
        path: "/",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <HomePage />
          </Suspense>
        ),
      },
      {
        path: "/one-to-one",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <OneToOneGames />
          </Suspense>
        ),
      },
      {
        path: "/tournaments",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureTournament />
          </Suspense>
        ),
      },
      {
        path: "/leadership-board",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LeaderShipBoard />
          </Suspense>
        ),
      },

      {
        path: "/edit-user-profile",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EditUserProfile />
          </Suspense>
        ),
      },

      {
        path: "/academy",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Academy />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/contact-us",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ContactUs />
          </Suspense>
        ),
      },
      {
        path: "/about-us",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AboutUsDetails />
          </Suspense>
        ),
      },

      {
        path: "/join-our-community",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <JoinOurCommunty />
          </Suspense>
        ),
      },
      {
        path: "/login",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: "/sign-up",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SignUp />
          </Suspense>
        ),
      },

      {
        path: "/terms-conditions",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <TermsAndConditions />
          </Suspense>
        ),
      },

      {
        path: "/forgot-password",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ForgotPassword />
          </Suspense>
        ),
      },
      {
        path: "/verify-otp",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <VerifyOtp />
          </Suspense>
        ),
      },
      {
        path: "/profile",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Consumerprofile />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/achievements",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AllAchievements />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/achievements/:userId",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AllAchievements />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/send-gift/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SendGift />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/friends",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Friends />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/friends/:userId",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Friends />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/teams/:userId",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AllTeams />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/game-types/:userId",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AllGameTypes />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/wallet",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Wallet />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/messages",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Message />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/chat-reactions",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AdminChatReactions />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/join-tournament",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <JoinTournament />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/consumer-Profile/:id",
        element: (
          // <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <Consumerprofile />
          </Suspense>
          // </ProtectedRoute>
        ),
      },
      {
        path: "/register-featured/:id",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeaturedRegiter />
          </Suspense>
        ),
      },
      {
        path: "/register-event/:id",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RegisterEvent />
          </Suspense>
        ),
      },
      {
        path: "/all-tournaments",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Tournaments />
          </Suspense>
        ),
      },
      {
        path: "/my-tournaments",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Tournaments />
          </Suspense>
        ),
      },
      {
        path: "/result/:id/:currentRound/:opponentId",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Result />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/one-to-one-message/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <OneToOneGameRoom />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/one-to-one-room-details/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <OneToOneRoomDetails />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/create-duel-room",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <CreateDuelRoom />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/one-to-one-score-submission",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <ScoreSubmissionOneToOne />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/duels/lobby",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <OneToOneLobby />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/events",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Events />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/random-play",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <RandomPlay />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/view-duels-rooms",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AllRooms />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/game-rules/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <OneToOneGameRules />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/store",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Store />
          </Suspense>
        ),
      },
      {
        path: "/all-products",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AllProductsPage />
          </Suspense>
        ),
      },
      {
        path: "/product-details",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProductDetailsPage />
          </Suspense>
        ),
      },
      {
        path: "/my-cart",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <CartPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/my-orders",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <OrdersList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/order-details",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <OrderDetails />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      {
        path: "/rewards",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Rewards />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      {
        path: "/delete-account",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DeleteAccount />
          </Suspense>
        ),
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

function App() {
  const { isLoading } = useSelector((state) => state.loader);
  // const dispatch = useDispatch();
  const { t } = useTranslation("common");

  // Handle default language
  const defaultLanguage = getLocalStorageData("language") || "ar";
  if (defaultLanguage) {
    setLocalStorageData("language", defaultLanguage);
  }

  // Handle ads properly
  // const storedAds = getLocalStorageData("ads");
  // const parsedAds = storedAds === "false" ? false : storedAds == null ? true : !!storedAds;
  // useEffect(() => {
  //   dispatch(setAdsState(parsedAds));
  // }, [dispatch, parsedAds]);

  useEffect(() => {
    document.title = t("website_heading.page_title");
  }, [t]);

  return (
    <>
      {isLoading ? <LoaderSpinner /> : null}
      <RouterProvider router={router}>

        <GoogleAnalytics />
      </RouterProvider>
    </>
  );
}

export default App;
